import React, { useState } from 'react';
import axios from 'axios';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getUserToken } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Director 13 Oct 2026: a customer may delete one of their own diary entries. Confirmation first; the photo is destroyed at once.
// A legal hold (account or entry) is refused by the server with a clear message and nothing is removed.
export const DeleteEntryButton = ({ entry, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const stop = (e) => e.stopPropagation();

  const confirm = async (e) => {
    stop(e);
    setBusy(true);
    try {
      const res = await axios.delete(`${API_URL}/api/food-entries/${entry.id}`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      toast.success(res.data.photo === 'destroyed' ? 'Entry deleted and its photo destroyed.' : 'Entry deleted.');
      setOpen(false);
      if (onDeleted) onDeleted(entry.id);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not delete this entry.';
      toast.error(msg, { duration: err.response?.status === 423 ? 10000 : 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          onClick={stop}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 text-red-600 shadow-lg hover:bg-red-50 transition-colors"
          aria-label="Delete this entry"
          title="Delete this entry"
          data-testid={`delete-entry-${entry.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent onClick={stop} data-testid="delete-entry-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this diary entry?</AlertDialogTitle>
          <AlertDialogDescription>
            The entry is removed from your diary and its photo is destroyed immediately. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={stop} disabled={busy} data-testid="delete-entry-cancel">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="delete-entry-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete entry
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
